/**
 * Single source of truth for the sidebar.
 *
 * Items are grouped into sections so a 21-item menu stays scannable. Each item
 * declares the permission needed to see it: Layout renders only what the user
 * can open, hides a section whose items are all hidden, and PrivateRoute uses
 * the same list to decide where to send someone whose landing page is
 * off-limits.
 */
export const NAV_SECTIONS = [
  {
    key: 'overview',
    label: 'Overview',
    items: [
      { path: '/sy-admin', label: 'Dashboard', icon: 'dashboard', permission: 'dashboard.view', exact: true },
    ],
  },
  {
    key: 'catalogue',
    label: 'Catalogue',
    items: [
      { path: '/sy-admin/hotels', label: 'Hotels', icon: 'hotel', permission: 'hotels.view' },
      { path: '/sy-admin/spas', label: 'Spas', icon: 'spa', permission: 'spas.view' },
      { path: '/sy-admin/tours', label: 'Tours & Activities', icon: 'tour', permission: 'tours.view' },
      { path: '/sy-admin/travel-packages', label: 'Travel Packages', icon: 'package', permission: 'travel-packages.view' },
    ],
  },
  {
    key: 'taxonomy',
    label: 'Listing setup',
    items: [
      { path: '/sy-admin/facilities', label: 'Facilities', icon: 'facility', permission: 'facilities.view' },
      { path: '/sy-admin/amenities', label: 'Amenities', icon: 'amenity', permission: 'amenities.view' },
      { path: '/sy-admin/hotel-categories', label: 'Hotel Categories', icon: 'tag', permission: 'hotel-categories.view' },
      { path: '/sy-admin/suggested-destinations', label: 'Destinations', icon: 'pin', permission: 'suggested-destinations.view' },
      { path: '/sy-admin/cities', label: 'Cities', icon: 'map', permission: 'cities.view' },
    ],
  },
  {
    key: 'audience',
    label: 'Audience',
    items: [
      { path: '/sy-admin/customers', label: 'Customers', icon: 'users', permission: 'customers.view' },
      { path: '/sy-admin/wishlists', label: 'Wishlists', icon: 'heart', permission: 'wishlists.view' },
      { path: '/sy-admin/push-notifications', label: 'Push Notifications', icon: 'bell', permission: 'push-notifications.view' },
    ],
  },
  {
    key: 'revenue',
    label: 'Revenue',
    items: [
      { path: '/sy-admin/verification-subscriptions', label: 'Verification Plans', icon: 'verified', permission: 'verification-subscriptions.view' },
      { path: '/sy-admin/featured-subscriptions', label: 'Featured Plans', icon: 'star', permission: 'featured-subscriptions.view' },
      { path: '/sy-admin/subscription-history', label: 'Subscription History', icon: 'history', permission: 'subscription-history.view' },
    ],
  },
  {
    key: 'inbox',
    label: 'Inbox',
    items: [
      { path: '/sy-admin/owner-registrations', label: 'Owner Registrations', icon: 'clipboard', permission: 'owner-registrations.view' },
      { path: '/sy-admin/listing-changes', label: 'Change Requests', icon: 'edit', permission: 'listing-changes.view' },
      { path: '/sy-admin/reports', label: 'Reports', icon: 'flag', permission: 'reports.view' },
    ],
  },
  {
    key: 'system',
    label: 'System',
    items: [
      { path: '/sy-admin/legal-pages', label: 'Legal Pages', icon: 'doc', permission: 'legal-pages.view' },
      { path: '/sy-admin/roles', label: 'Roles & Permissions', icon: 'shield', permission: 'roles.view' },
      { path: '/sy-admin/employees', label: 'Employees', icon: 'badge', permission: 'employees.view' },
      { path: '/sy-admin/settings', label: 'Settings', icon: 'settings', permission: 'settings.view' },
    ],
  },
];

/** Flat list, in sidebar order. */
const NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

/**
 * First page this user is actually allowed to open. Used after login and when
 * someone lands on a page their role does not cover.
 */
export const getLandingRoute = (hasPermission) => {
  const item = NAV_ITEMS.find((nav) => hasPermission(nav.permission));
  return item ? item.path : null;
};

export default NAV_ITEMS;
